// Avatar and pawn catalogs, and the per-profile selection stored locally.
import { escapeHTML } from "../lib/dom.js";
import { state } from "../state.js";

export function profileSchoolLabel(profileID) {
  const enrollment = state.enrollments.find(item => item.child_profile_id === profileID);
  if (!enrollment) return state.language === "it" ? "Scuola non impostata" : "School not set";
  return `${enrollment.school_name}, ${enrollment.school_city} · ${enrollment.class_name}`;
}

export function pawnOptions(style = selectedAvatarStyle()) {
  const male = [
    { code: "fox", emoji: "🦊", labelIT: "Volpino", labelEN: "Fox" },
    { code: "robot", emoji: "🤖", labelIT: "Robot", labelEN: "Robot" },
    { code: "dragon", emoji: "🐉", labelIT: "Draghetto", labelEN: "Dragon" },
    { code: "astronaut_boy", emoji: "👨‍🚀", labelIT: "Astronauta", labelEN: "Astronaut" },
    { code: "wizard_boy", emoji: "🧙‍♂️", labelIT: "Mago", labelEN: "Wizard" },
    { code: "super_boy", emoji: "🦸‍♂️", labelIT: "Supereroe", labelEN: "Superhero" }
  ];
  const female = [
    { code: "cat", emoji: "🐱", labelIT: "Gattina", labelEN: "Cat" },
    { code: "unicorn", emoji: "🦄", labelIT: "Unicorno", labelEN: "Unicorn" },
    { code: "butterfly", emoji: "🦋", labelIT: "Farfalla", labelEN: "Butterfly" },
    { code: "astronaut_girl", emoji: "👩‍🚀", labelIT: "Astronauta", labelEN: "Astronaut" },
    { code: "wizard_girl", emoji: "🧙‍♀️", labelIT: "Maga", labelEN: "Wizard" },
    { code: "super_girl", emoji: "🦸‍♀️", labelIT: "Supereroina", labelEN: "Superhero" }
  ];
  return style === "female" ? female : male;
}

export function pawnStorageKey(profileID = state.profile?.id) {
  return `brainmath-pawn-${profileID || "default"}`;
}

export function selectedPawn(profileID = state.profile?.id) {
  const options = pawnOptions(selectedAvatarStyle(profileID));
  const code = localStorage.getItem(pawnStorageKey(profileID)) || "fox";
  return options.find(item => item.code === code) || options[0];
}

export function pawnLabel(pawn) {
  return state.language === "it" ? pawn.labelIT : pawn.labelEN;
}

export function selectedAvatarStyle(profileID = state.profile?.id) {
  const profile = state.profile?.id === profileID
    ? state.profile
    : profileID
      ? state.profiles.find(item => item.id === profileID)
      : state.profile;
  const normalizeStyle = value => {
    const normalized = String(value || "").trim().toLowerCase();
    if (["female", "f", "girl", "femmina"].includes(normalized)) return "female";
    if (["male", "m", "boy", "maschio"].includes(normalized)) return "male";
    return null;
  };
  const profileStyle = normalizeStyle(profile?.profile_type);
  if (profileStyle) return profileStyle;
  return "male";
}

export function learnerAdventureScene(profileID = state.profile?.id) {
  return selectedAvatar(profileID).image;
}

export function avatarOptions(style = selectedAvatarStyle()) {
  const male = [
    { code: "boy-explorer", emoji: "👦", image: "/brainmath-avatars/boy-explorer.webp?v=20260810", labelIT: "Esploratore", labelEN: "Explorer" },
    { code: "boy-scientist", emoji: "🧑‍🔬", image: "/brainmath-avatars/boy-scientist.webp?v=20260810", labelIT: "Scienziato", labelEN: "Scientist" },
    { code: "boy-astronaut", emoji: "👨‍🚀", image: "/brainmath-avatars/boy-astronaut.webp?v=20260810", labelIT: "Astronauta", labelEN: "Astronaut" },
    { code: "boy-champion", emoji: "🏅", image: "/brainmath-avatars/boy-champion.webp?v=20260810", labelIT: "Campione", labelEN: "Champion" },
    { code: "boy-wizard", emoji: "🧙‍♂️", image: "/brainmath-avatars/boy-wizard.webp?v=20260810", labelIT: "Mago", labelEN: "Wizard" },
    { code: "boy-reader", emoji: "📚", image: "/brainmath-avatars/boy-reader.webp?v=20260810", labelIT: "Lettore", labelEN: "Reader" }
  ];
  const female = [
    { code: "girl-explorer", emoji: "👧", image: "/brainmath-avatars/girl-explorer.webp?v=20260810", labelIT: "Esploratrice", labelEN: "Explorer" },
    { code: "girl-scientist", emoji: "👩‍🔬", image: "/brainmath-avatars/girl-scientist.webp?v=20260810", labelIT: "Scienziata", labelEN: "Scientist" },
    { code: "girl-astronaut", emoji: "👩‍🚀", image: "/brainmath-avatars/girl-astronaut.webp?v=20260810", labelIT: "Astronauta", labelEN: "Astronaut" },
    { code: "girl-champion", emoji: "🏅", image: "/brainmath-avatars/girl-champion.webp?v=20260810", labelIT: "Campionessa", labelEN: "Champion" },
    { code: "girl-wizard", emoji: "🧙‍♀️", image: "/brainmath-avatars/girl-wizard.webp?v=20260810", labelIT: "Maga", labelEN: "Wizard" },
    { code: "girl-reader", emoji: "📚", image: "/brainmath-avatars/girl-reader.webp?v=20260810", labelIT: "Lettrice", labelEN: "Reader" }
  ];
  return style === "female" ? female : male;
}

export function selectedAvatar(profileID = state.profile?.id) {
  const style = selectedAvatarStyle(profileID);
  const options = avatarOptions(style);
  const profile = state.profile?.id === profileID
    ? state.profile
    : profileID
      ? state.profiles.find(item => item.id === profileID)
      : state.profile;
  const code = profile?.avatar_symbol;
  return options.find(item => item.code === code) || options[0];
}

export function avatarLabel(avatar) {
  return state.language === "it" ? avatar.labelIT : avatar.labelEN;
}

export function avatarPicture(profileID = state.profile?.id, className = "") {
  const avatar = selectedAvatar(profileID);
  return `<img class="profile-avatar-picture ${className}" src="${avatar.image}" alt="${escapeHTML(avatarLabel(avatar))}" decoding="async">`;
}
